import {
    ALL_VIEW,
    type BuiltInView,
    CASE_VIEWS,
    CONTACT_VIEWS,
    TIMECARD_VIEWS,
} from 'components/tables/list-table/filter-bar/builtInViews';
import { ASSIGNMENT_KEYWORDS_JOINT } from 'pages/timecards/assignmentKeywordsFilter';
import { FeatureFlag } from 'types/features';
import type { WidgetUnit } from 'types/homeWidgetFigures';
import type { ListKey } from 'types/listViews';
import { TaskStatusType } from 'types/tasks';
import type { UserData } from 'types/user';

/** Stands for the viewer's own id in a filter the list writes with it. */
const VIEWER = '{viewer}';

/** One number of a list's totals answer, named by its field there and labelled as the list's column is. */
export type ListTotal = { key: string; labelId: string; unit: Exclude<WidgetUnit, 'COUNT'> };

/**
 * A built-in view as a widget counts it: `searchParams` is what the list opens the view with when its URL names no
 * filter, `passiveFilter` what the list's page adds to every request while the view, or a saved view based on it, is
 * open.
 */
export type SavedViewBuiltIn = BuiltInView & { passiveFilter?: string };

/**
 * A field the list's page never sends as written: the value of the clause on `joinSelector` goes into the clause
 * of the field that hosts it, matched on each of `selectors`, as the list's own joint filter says.
 */
export type JointFields = { joinSelector: string; selectors: string[]; comperator?: string };

export type SavedViewList = {
    /** The list's name, as its menu entry reads. */
    labelId: string;
    /** Any one of them opens the list's page. */
    permissions: string[];
    /** The module a firm must have for the list to exist. */
    feature?: FeatureFlag;
    /** The page endpoint the list's rows come from. */
    url: string;
    /**
     * Where the list's own page reads its exact count once its page stops counting at the cap. Only where that count
     * is the list's: a list without one shows the capped count.
     */
    countUrl?: string;
    /** The date range field a period is written on. A list without one keeps its view's own dates. */
    dateField?: string;
    /** Its built-in views, the one the list opens on first. */
    views: SavedViewBuiltIn[];
    totals?: { url: string; fields: ListTotal[] };
    /** The fields its page folds into another's clause before sending, by the selector of the hosting field. */
    joint?: Record<string, JointFields>;
};

/**
 * The lists a saved-view widget can be built on, each with what its own page asks its endpoint for: the four whose
 * rows are totalled, and every other whose page endpoint says when its count stopped at the cap. A list is here only
 * if its page sends a view's filters as they stand, or folded as its `joint` says, so the widget's number is the
 * list's. The permissions and the feature are those of the list's route.
 */
export const SAVED_VIEW_LISTS: Partial<Record<ListKey, SavedViewList>> = {
    timecards: {
        labelId: 'timecards.timecards',
        permissions: ['READ.TIMECARDS.MY_OWN', 'READ.TIMECARDS.MATTER_ACCESS'],
        url: '/v1/timecards',
        countUrl: '/v1/timecards/count',
        dateField: 'timecardDate',
        views: [
            {
                ...TIMECARD_VIEWS[0],
                searchParams: 'qf=id=ismynondrafttimecard=true',
                passiveFilter: 'timecardStatusTypeCode=out=(DRAFT)',
            },
            {
                ...TIMECARD_VIEWS[1],
                passiveFilter: `timecardStatusTypeCode==DRAFT;createdByEmployee.id==(${VIEWER})`,
            },
        ],
        totals: {
            url: '/v1/timecards/totals',
            fields: [
                { key: 'totalUsedTimeInSeconds', labelId: 'timecards.list.columns.used-time', unit: 'HOURS' },
                { key: 'totalBilledTimeInSeconds', labelId: 'timecards.list.columns.billed-time', unit: 'HOURS' },
                { key: 'totalFeesInBaseCurrency', labelId: 'timecards.list.columns.fees-total', unit: 'AMOUNT' },
            ],
        },
        joint: { 'matter.id': ASSIGNMENT_KEYWORDS_JOINT },
    },
    invoices: {
        labelId: 'menu.invoices',
        permissions: ['READ.INVOICES.MATTER_ACCESS'],
        url: '/v1/invoices',
        dateField: 'invoiceDate',
        // The KSeF tab is left out: it exists only where the firm has KSeF, and shows the same invoices.
        views: [
            { id: 'main', labelId: 'invoices.table-view.global' },
            { id: 'law', labelId: 'invoices.table-view.law' },
            { id: 'rev', labelId: 'invoices.table-view.rev' },
            { id: 'cr', labelId: 'invoices.table-view.cr' },
            { id: 'acct', labelId: 'invoices.table-view.acct' },
        ],
        totals: {
            url: '/v1/invoices/calculate-totals',
            fields: [
                { key: 'totalFeesBaseCurrency', labelId: 'invoices.fees-total', unit: 'AMOUNT' },
                { key: 'totalExpensesBaseCurrency', labelId: 'invoices.expenses-total', unit: 'AMOUNT' },
                { key: 'totalAmountBaseCurrency', labelId: 'stats.total-amount', unit: 'AMOUNT' },
                { key: 'totalFinalPayableBaseCurrency', labelId: 'invoices.final-payable-amount', unit: 'AMOUNT' },
                { key: 'totalOutstandingBalanceBaseCurrency', labelId: 'invoices.outstanding-balance', unit: 'AMOUNT' },
            ],
        },
    },
    expenses: {
        labelId: 'menu.expenses',
        permissions: ['READ.EXPENSES.ALL', 'READ.EXPENSES.MY_OWN', 'READ.EXPENSES.MATTER_ACCESS'],
        url: '/v1/expenses',
        countUrl: '/v1/expenses/count',
        dateField: 'expenseDate',
        views: [ALL_VIEW],
        totals: {
            url: '/v1/expenses/calculate-totals',
            fields: [{ key: 'totalAmountInBaseCurrency', labelId: 'expenses.amount', unit: 'AMOUNT' }],
        },
    },
    'bank-transfers': {
        labelId: 'menu.bank-transfers',
        permissions: ['READ.BANK_TRANSACTIONS.ALL'],
        url: '/v1/bank-transactions',
        dateField: 'entryBookingDate',
        views: [ALL_VIEW],
        totals: {
            url: '/v1/bank-transactions/calculate-totals',
            fields: [{ key: 'entryAmountBreakdown', labelId: 'stats.total-amount', unit: 'AMOUNT' }],
        },
    },
    matters: {
        labelId: 'matters.matters',
        permissions: [
            'READ.MATTERS.MY_CASES',
            'READ.MATTERS.MY_TEAM',
            'READ.MATTERS.MY_TEAM.NON_CONFIDENTIAL',
            'READ.MATTERS.CONFIDENTIAL',
            'READ.MATTERS.NON_CONFIDENTIAL',
            'READ.MATTERS.NON_CONFIDENTIAL_MY_CONTACTS',
        ],
        url: '/v1/matters',
        countUrl: '/v1/matters/count',
        dateField: 'openedDate',
        views: CASE_VIEWS,
    },
    efforts: {
        labelId: 'menu.efforts',
        permissions: ['READ.EFFORTS.ALL', 'READ.EFFORTS.MY_OWN'],
        url: '/v1/efforts',
        countUrl: '/v1/efforts/count',
        dateField: 'effortDate',
        views: [{ ...ALL_VIEW, searchParams: 'qf=effortByEmployee.id=ismyeffort=true' }],
    },
    tasks: {
        labelId: 'menu.tasks',
        permissions: ['READ.TASKS.ALL', 'READ.TASKS.MY_OWN', 'READ.TASKS.CREATED_BY_ME'],
        url: '/v1/tasks',
        dateField: 'deadlineDate',
        views: [
            {
                ...ALL_VIEW,
                searchParams: `mf=taskStatusTypeCode=in=(${TaskStatusType.IN_PROGRESS})&qf=id=istaskassignedtouser=${VIEWER}`,
            },
        ],
    },
    contacts: {
        labelId: 'contacts.contacts',
        permissions: ['READ.PARTIES.ALL', 'READ.PARTIES.MY_CONTACTS', 'READ.PARTIES.RELATED_TO_MY_CASES'],
        url: '/v1/parties',
        countUrl: '/v1/parties/count',
        views: CONTACT_VIEWS,
    },
    contracts: {
        labelId: 'contracts.contracts',
        permissions: ['READ.CONTRACTS.PARTY_ACCESS'],
        url: '/v1/contracts',
        countUrl: '/v1/contracts/count',
        views: [ALL_VIEW],
    },
    projects: {
        labelId: 'projects.projects',
        permissions: ['READ.PROJECTS'],
        url: '/v1/projects',
        views: [ALL_VIEW],
    },
    correspondence: {
        labelId: 'menu.correspondence',
        permissions: [
            'READ.CORRESPONDENCE.ALL',
            'READ.CORRESPONDENCE.MY_CASES',
            'READ.CORRESPONDENCE.CREATED_BY_ME',
            'READ.CORRESPONDENCE.MY_OWN',
        ],
        feature: FeatureFlag.CORRESPONDENCE_MODULE,
        url: '/v1/correspondence',
        dateField: 'date',
        views: [ALL_VIEW],
    },
    'experience-records': {
        labelId: 'experience-records.experience-records',
        permissions: ['READ.EXPERIENCE_RECORDS.ALL'],
        url: '/v1/matter-experience-records',
        views: [{ ...ALL_VIEW, searchParams: 'qf=matter.isExcludedFromXR==false' }],
    },
    employees: {
        labelId: 'menu.colleagues',
        permissions: ['READ.EMPLOYEES'],
        url: '/v1/employees',
        views: [{ ...ALL_VIEW, searchParams: 'qf=isActive=in=(true)' }],
    },
    pricelists: {
        labelId: 'menu.pricelists',
        permissions: ['READ.PRICELISTS.ALL', 'READ.PRICELISTS.MODEL', 'READ.PRICELISTS.NON_MODEL_WITH_ACCESS'],
        url: '/v1/pricelists',
        views: [ALL_VIEW],
    },
};

/** A registry filter as the viewer's list writes it. */
export function forViewer(filter: string | undefined, viewerId: number | undefined): string {
    return (filter ?? '').replaceAll(VIEWER, String(viewerId ?? ''));
}

/** Whether the viewer can open the list's page. */
export function canOpenList(
    { permissions, feature }: SavedViewList,
    user: Pick<UserData, 'permissions' | 'enabledFeatures'> | null | undefined
): boolean {
    if (feature && !user?.enabledFeatures?.includes(feature)) return false;
    return !!user?.permissions?.some(({ code }) => permissions.includes(code));
}
